/**
 * ERPContextBar — Compact ERP context strip shown on ALL screens.
 *
 * Always-visible layer (from sessionSlice): Company | Location | Financial Year | User
 * Transaction overlay (from erpContextSlice): Period | Currency | Document # | Tx Type
 *
 * The bar reads from both Redux slices automatically. Session info is always present
 * after login; transaction-specific info is shown when a document form is open.
 */

import { useSelector } from 'react-redux';
import { Building2, MapPin, CalendarRange, Hash, DollarSign, UserCircle, LayoutGrid } from 'lucide-react';
import type { RootState } from '@/state/store';
import { cn } from '@/utils/cn';

interface ContextItemProps {
  icon:    React.ReactNode;
  label:   string;
  value:   string | null | undefined;
  mono?:   boolean;
  strong?: boolean;
}

const ContextItem = ({ icon, label, value, mono, strong }: ContextItemProps) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5 px-3 border-r border-slate-200 dark:border-slate-600 last:border-r-0">
      <span className="text-slate-400 dark:text-slate-500 flex-shrink-0">{icon}</span>
      <div className="flex flex-col leading-tight">
        <span className="text-[9px] font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide">
          {label}
        </span>
        <span className={cn(
          'text-xs text-slate-700 dark:text-slate-200',
          mono   && 'font-mono',
          strong && 'font-semibold text-slate-900 dark:text-white',
        )}>
          {value}
        </span>
      </div>
    </div>
  );
};

interface ERPContextBarProps {
  className?: string;
}

export const ERPContextBar = ({ className }: ERPContextBarProps) => {
  const session  = useSelector((s: RootState) => s.session.session);
  const ctx      = useSelector((s: RootState) => s.erpContext.context);
  const authUser = useSelector((s: RootState) => s.auth.user);

  // Don't render if not logged in at all
  if (!session && !authUser) return null;

  // ── Derive display values ─────────────────────────────────────────────────

  // Company: prefer session, fall back to auth user company ID
  const companyDisplay = session
    ? `${session.companyCode} — ${session.companyName}`
    : null;

  // Location: from session
  const locationDisplay = session?.locationName ?? session?.locationCode ?? null;

  // Financial year: from session or from erpContext
  const fyDisplay = session?.financialYearCode
    ?? (ctx ? ctx.financialYearCode : null);

  // Period: from erpContext (transaction-specific)
  const periodDisplay = ctx
    ? `${ctx.financialYearCode} / ${ctx.financialPeriodCode}`
    : (fyDisplay ?? null);

  // Currency: from erpContext (transaction-specific)
  const currencyDisplay = ctx
    ? (ctx.exchangeRate !== 1
        ? `${ctx.currencyCode} @ ${ctx.exchangeRate.toFixed(4)}`
        : ctx.currencyCode)
    : null;

  // Document number: from erpContext
  const docNumber = ctx?.documentNumber ?? null;

  // Cost centre: from erpContext
  const ccDisplay = ctx?.costCenterCode
    ? (ctx.costCenterName ?? ctx.costCenterCode)
    : null;

  // User
  const userLabel = authUser?.fullName ?? authUser?.username ?? null;
  const roleLabel = authUser?.roles?.[0] ?? null;
  const userDisplay = userLabel
    ? (roleLabel ? `${userLabel} · ${roleLabel}` : userLabel)
    : null;

  return (
    <div className={cn(
      'flex items-stretch h-10 overflow-x-auto',
      'bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700',
      'text-xs select-none scrollbar-thin',
      className,
    )}>

      {/* Company — always from session */}
      {companyDisplay && (
        <ContextItem
          icon={<Building2 size={11} />}
          label="Company"
          value={companyDisplay}
          strong
        />
      )}

      {/* Location — from session */}
      {locationDisplay && (
        <ContextItem
          icon={<MapPin size={11} />}
          label="Location"
          value={locationDisplay}
        />
      )}

      {/* Cost Centre — from erpContext (transaction-level) */}
      {ccDisplay && (
        <ContextItem
          icon={<LayoutGrid size={11} />}
          label="Cost Centre"
          value={ccDisplay}
        />
      )}

      {/* Period / Financial Year */}
      <ContextItem
        icon={<CalendarRange size={11} />}
        label={ctx ? 'Period' : 'Fin. Year'}
        value={periodDisplay}
        mono
      />

      {/* Currency — only when in a transaction */}
      {currencyDisplay && (
        <ContextItem
          icon={<DollarSign size={11} />}
          label="Currency"
          value={currencyDisplay}
          mono
        />
      )}

      {/* Document Number — transaction-specific */}
      {docNumber && (
        <ContextItem
          icon={<Hash size={11} />}
          label="Document"
          value={docNumber}
          mono
          strong
        />
      )}

      {/* User */}
      {userDisplay && (
        <ContextItem
          icon={<UserCircle size={11} />}
          label="User"
          value={userDisplay}
        />
      )}

      {/* Tx Type — right-aligned, only when in a transaction */}
      {ctx?.transactionTypeCode && (
        <div className="ml-auto flex items-center px-3 border-l border-slate-200 dark:border-slate-600">
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold
                           bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            {ctx.transactionTypeCode}
          </span>
        </div>
      )}
    </div>
  );
};
